import React, { useEffect, useState } from "react";
import useConfirm from "../../../../hooks/useConfirm";
import { Plus } from "react-bootstrap-icons";
import { Edit2, Trash2, GitBranch } from "lucide-react";
import ModalWrapper from "../../../../components/learning-development/common/ModalWrapper";

import DynamicTable from "../../../../components/learning-development/common/DynamicTable";
import CertificateUploadModal from "./CertificateUploadModal";
import api from "../../../../Axios";
import { toast } from "react-toastify";
import { defaultDateFormatter } from "../../../../Helper/functionHelper";
import ExpandableText from "../../../../components/common/ExpandableText";
import CommonFilter from "../../../../components/common/CommonFilter";
import {
  TextField,
  Stack,
  Tooltip
} from "@mui/material";
import UploadIcon from '@mui/icons-material/Upload';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import useModal from "../../../../hooks/useModal";
import useLoader from "../../../../hooks/useLoader";

const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const ExternalLearningRequestList = () => {

  const { hideLoader, showLoader } = useLoader();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [documentPath, setDocumentPath] = useState("");
  const { showModal } = useModal();

  const [modalType, setModalType] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestFlow, setRequestFlow] = useState([]);

  const [form, setForm] = useState({
    learning_title: "",
    learning_provider: "",
    learning_url: "",
    description: "",
    estimated_cost: 0
  });
  const [errors, setErrors] = useState({});
  const [filters, setFilters] = useState({
    search: "",
    status: "",
    from_date: null,
    to_date: null
  });

  const [filterConfig, setFilterConfig] = useState([
    {
      name: "search",
      type: "text",
      label: "Program title/provider",
      grid: 2,
    },
    {
      name: "status",
      type: "select",
      label: "Program Status",
      grid: 2,
      options: [
        { label: "Draft", value: "draft" },
        { label: "Created", value: "created" },
        { label: "Progress", value: "progress" },
        { label: "Approved", value: "approved" },
        { label: "Rejected", value: "rejected" },
      ]
    },
    {
      name: "from_date",
      type: "date",
      label: "From Date",
      grid: 2,
    },
    {
      name: "to_date",
      type: "date",
      label: "To Date",
      grid: 2,
    },
  ]);

  const fetchRequests = async (page = currentPage, customFilters = filters) => {
    try {
      showLoader();

      const res = await api.post("/lnd/elr/get-all", {
        page,
        limit: itemsPerPage,
        searchData: {
          ...customFilters,
          search: customFilters.search?.trim() || ""
        }
      });

      const data = res.data?.data?.data || [];

      const pagination = res?.data?.data?.pagination;

      setRequests(data || []);
      setTotalPages(pagination.totalPages);
      setTotalItems(pagination.total);
      setDocumentPath(res?.data?.data?.documentPath || "");
    } catch (err) {
      console.log(err, "err");
      toast.error(err?.response?.data?.message || "Failed to load requests");
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchRequests(currentPage, filters);
  }, [currentPage, itemsPerPage, search]);

  const openModal = async (type, row) => {
    try {
      const res = await api.get(`/approval/get-approval-request-flow?request_id=${row['approval.id']}`);
      setRequestFlow(res?.data?.data?.workflow?.data || []);
    } catch (error) {
      console.log("Error fetching request flow:", error);
      toast.error(error?.response?.data?.message || "Failed to load approval flow");
    }
    setSelectedRequest(row);
    setModalType(type);
  };

  const validateForm = () => {
    let newErrors = {};
    if (!form.learning_title.trim()) newErrors.learning_title = "Learning Title is required";
    if (!form.learning_url.trim()) newErrors.learning_url = "Learning URL is required";
    if (!form.description.trim()) newErrors.description = "Description is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = async (status = "created") => {
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      await api.post("/lnd/elr/create", { ...form, status });
      toast.success(status === "draft" ? "Request draft saved successfully" : "Request created successfully");
      closeModal();
      await fetchRequests(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to create request");
    }
    finally {
      setLoading(false);
    }
  };


  const handleUpdate = async () => {
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      await api.put(
        `/lnd/elr/update-request/${selectedRequest.id}`,
        form
      );
      toast.success("Request updated");
      closeModal();
      fetchRequests(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update request");
    }
    finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    setLoading(true);
    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      await api.put(
        `/lnd/elr/update-request/${selectedRequest.id}`,
        { ...form, status: "created" }
      );
      toast.success("Request updated");
      closeModal();
      fetchRequests(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update request");
    }
    finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/lnd/elr/delete-request/${selectedRequest.id}`);
      toast.success("Deleted successfully");
      closeModal();
      fetchRequests(currentPage, filters);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  const openCreateModal = () => {
    setForm({
      learning_title: "",
      learning_provider: "",
      learning_url: "",
      description: "",
      estimated_cost: 0
    });
    setErrors({});
    setModalType("create");
  };

  const openEditModal = (row) => {
    setSelectedRequest(row);
    setForm({
      learning_title: row.learning_title,
      learning_provider: row.learning_provider || "",
      learning_url: row.learning_url,
      description: row.description || "",
      estimated_cost: row.estimated_cost || 0
    });
    setErrors({});
    setModalType("edit");
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch (err) {
      return false;
    }
  };

  const openDeleteModal = (row) => {
    setSelectedRequest(row);
    setModalType("delete");
  };

  const closeModal = () => {
    setModalType(null);
    setSelectedRequest(null);
  };

  const handleCertficateOpen = (key, title, certificate_img = "") => {
    let body;

    if (key === "certificate") {
      body = (
        <div className="text-center">
          {certificate_img ? (
            <img
              src={BASE_URL + documentPath + certificate_img}
              alt="Certificate"
              style={{
                maxWidth: "100%",
                maxHeight: "500px",
                borderRadius: "8px"
              }}
            />
          ) : (
            <p>No certificate available</p>
          )}
        </div>
      );
    }

    showModal(title, body);
  };

  const columns = [
    {
      label: "Title",
      render: (row) => (
        <ExpandableText text={row.learning_title} label="Request Title" />
      )
    },
    {
      label: "Provider",
      render: (row) => (
        row.learning_provider ? row.learning_provider : "--"
      )
    },
    {
      label: "Description",
      render: (row) => (
        <ExpandableText text={row.description || "--"} label="Request Description" />
      )
    },
    {
      label: "Cost(₹)",
      render: (row) => row.estimated_cost ? `${row.estimated_cost}` : "--"
    },
    {
      label: "Final Approval Status",
      render: (row) => (
        <span className={`text-${row["approval.status"] === "APPROVED" ? "success" : row["approval.status"] === "REJECTED" ? "danger" : "warning"}`}>
          {row["approval.status"]}
        </span>
      )
    },
    {
      label: "Upload Certificate",
      render: (row) => {
        if (row["approval.status"] === "APPROVED") {
          return (
            <div className="d-flex">
              <Tooltip title="Upload" placement="top" arrow>
                <UploadIcon
                  className="cursor-pointer"
                  sx={{ color: "black" }}
                  onClick={() => openModal("certificate", row)}
                />
              </Tooltip>

              {row?.certificate_img && <Tooltip title="View" placement="top" arrow>
                <RemoveRedEyeIcon
                  className="cursor-pointer"
                  sx={{ color: "black", marginLeft: "8px" }}
                  onClick={() => handleCertficateOpen("certificate", "Certificate", row?.certificate_img)}
                />
              </Tooltip>}
            </div>
          );
        }
        return "--";
      }
    },
    {
      label: "Program Status",
      render: (row) => {
        const formatStatus = (status) => {
          if (!status) return "";

          return status.charAt(0).toUpperCase() + status.slice(1);
        };

        return (
          <span>
            {formatStatus(row.status)}
          </span>
        );
      }
    },
    {
      label: "Rejection Reason",
      render: (row) => (
        <ExpandableText text={row["approval.reject_reason"] || "--"} label="Rejection Reason" />
      )
    },
    {
      label: "Requested At",
      render: (row) =>
        defaultDateFormatter(row.created_at)
    },
    {
      label: "Actions",
      render: (row) => {
        const isEditDeleteVisible = (row.status === "draft" || row.status === "created") ? true : false;
        // const isSendButtonVisible = row.status === "draft";

        return (
          <div className="d-flex gap-2">
            {isEditDeleteVisible && <div
              className="cursor-pointer"
              onClick={() => openEditModal(row)}
            >
              <Edit2 size={16} />
            </div>}

            {row.status !== "draft" && <div
              className="cursor-pointer"
              onClick={() => openModal("track", row)}
            >
              <GitBranch size={16} />
            </div>}

            {isEditDeleteVisible && <div
              className="cursor-pointer"
              onClick={() => openDeleteModal(row)}
            >
              <Trash2 size={16} />
            </div>}
          </div>
        );
      },
    }
  ];

  return (
    <div className="container-fluid py-4">

      {/* Header */}

      <div className="row g-2 align-items-end mb-4">

        {/* FILTER SECTION */}
        <div className="col-12 col-lg">
          <CommonFilter
            config={filterConfig}
            defaultValues={{
              search: "",
              status: "",
              from_date: null,
              to_date: null
            }}
            onSubmit={(data) => {
              setFilters(data);
              setCurrentPage(1);
              fetchRequests(1, data);
            }}
          // onExport={handleExport}
          />
        </div>

        {/* CREATE BUTTON */}
        <div className="col-12 col-lg-auto d-flex justify-content-end">
          <button
            onClick={openCreateModal}
            className="btn btn-success d-flex align-items-center justify-content-center"
            style={{ width: "40px", height: "40px" }}
          >
            <Plus size={24} />
          </button>
        </div>

      </div>

      <DynamicTable
        columns={columns}
        data={requests}
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={totalItems}
        onPageChange={(page) => setCurrentPage(page)}
        rowKey="id"
        loading={loading}
      />

      {(modalType === "create" || modalType === "edit") && (
        <div
          className="modal fade show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content p-4">

              {/* HEADER */}
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">
                  {modalType === "create" ? "Create Request" : "Edit Request"}
                </h5>

                <span
                  style={{
                    cursor: "pointer",
                    fontSize: "22px",
                    fontWeight: "bold",
                    lineHeight: "1"
                  }}
                  onClick={closeModal}
                >
                  ×
                </span>
              </div>

              <Stack spacing={2}>

                {/* Learning Title */}
                <TextField
                  label="Learning Title *"
                  fullWidth
                  size="small"
                  value={form.learning_title}
                  error={!!errors.learning_title}
                  helperText={errors.learning_title}
                  onChange={(e) => {
                    setForm({ ...form, learning_title: e.target.value });
                    if (errors.learning_title) setErrors({ ...errors, learning_title: "" });
                  }}
                />

                {/* Provider */}
                <TextField
                  label="Provider"
                  fullWidth
                  size="small"
                  value={form.learning_provider}
                  onChange={(e) =>
                    setForm({ ...form, learning_provider: e.target.value })
                  }
                />

                {/* Learning URL */}
                <TextField
                  label="Learning URL *"
                  fullWidth
                  size="small"
                  value={form.learning_url}
                  error={!!errors.learning_url}
                  helperText={errors.learning_url}
                  onChange={(e) => {
                    const value = e.target.value;

                    setForm({ ...form, learning_url: value });

                    if (!value) {
                      setErrors({ ...errors, learning_url: "URL is required" });
                    } else if (!isValidUrl(value)) {
                      setErrors({ ...errors, learning_url: "Enter a valid URL (include http/https)" });
                    } else {
                      setErrors({ ...errors, learning_url: "" });
                    }
                  }}
                />

                {/* Estimated Cost */}
                <TextField
                  label="Estimated Cost(₹)"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.estimated_cost}
                  inputProps={{ min: 0 }}
                  onChange={(e) =>
                    setForm({ ...form, estimated_cost: e.target.value })
                  }
                />

                {/* Description */}
                <TextField
                  label="Description *"
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  value={form.description}
                  error={!!errors.description}
                  helperText={errors.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />

                {/* Buttons */}
                <div className="d-flex justify-content-end gap-3 mt-4">

                  {modalType === "create" && (
                    <button
                      className="btn btn-secondary btn-sm px-3 fw-medium"
                      onClick={() => handleCreate("draft")}
                      disabled={loading}
                    >
                      Save as Draft
                    </button>
                  )}

                  {modalType === "edit" && selectedRequest?.status === "draft" && (
                    <button
                      className="btn btn-secondary btn-sm px-3 fw-medium"
                      onClick={handlePublish}
                      disabled={loading}
                    >
                      Publish Draft
                    </button>
                  )}

                  <button
                    className="btn btn-outline-secondary btn-sm px-3 fw-medium"
                    onClick={closeModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-success btn-sm px-3 fw-medium"
                    onClick={
                      modalType === "create"
                        ? () => handleCreate("created")
                        : handleUpdate
                    }
                    disabled={loading}
                  >
                    {modalType === "create" ? "Create Request" : "Update Request"}
                  </button>

                </div>

              </Stack>

            </div>
          </div>
        </div>
      )}

      {/* CERTIFICATE UPLOAD MODAL */}
      {modalType === "certificate" && (
        <CertificateUploadModal
          isOpen={true}
          onClose={closeModal}
          certificateUrl={selectedRequest?.certificate_url || null}
          fetchRequests={fetchRequests}
          requestId={selectedRequest?.id}
        />
      )}

      {/* TRACK FLOW MODAL */}
      {modalType === "track" && (() => {

        const formatFlow = (data) => {
          return data.map((item, index) => {
            let label = "";

            if (index === 0) {
              label = "Requested by Employee";
            } else {
              const actionText =
                item.action === "APPROVE"
                  ? "Approved"
                  : item.action === "REJECT"
                    ? "Rejected"
                    : "";

              label = `${actionText} by ${item?.role?.role_title || ""}`;
            }

            return {
              label,
              date: item.created_at,
              status:
                item.action === "REJECT"
                  ? "rejected"
                  : "completed",
            };
          });
        };

        const steps = formatFlow(requestFlow || []);

        const rejectedIndex = steps.findIndex(
          (s) => s.status === "rejected"
        );

        const totalSteps = steps.length;

        const progressWidth =
          rejectedIndex !== -1
            ? ((rejectedIndex + 1) / totalSteps) * 100
            : (totalSteps / totalSteps) * 100;

        const progressColor =
          rejectedIndex !== -1 ? "#dc3545" : "#198754";

        return (
          <ModalWrapper onClose={closeModal}>

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">

              <h5 className="fw-bold mb-0">Approval Flow</h5>

              <button
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "20px",
                  cursor: "pointer",
                  lineHeight: "1"
                }}
              >
                ✕
              </button>

            </div>

            {/* FLOW */}
            <div>

              <div className="d-flex justify-content-between align-items-center position-relative">

                {/* Base Line */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "0",
                    right: "0",
                    height: "2px",
                    background: "#dee2e6",
                    zIndex: 0,
                  }}
                />

                {/* Progress Line */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "0",
                    width: `${progressWidth}%`,
                    height: "2px",
                    background: progressColor,
                    zIndex: 1,
                  }}
                />

                {/* Steps */}
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center flex-fill position-relative"
                  >

                    {/* Circle */}
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor:
                          step.status === "completed"
                            ? "#198754"
                            : step.status === "rejected"
                              ? "#dc3545"
                              : "#adb5bd",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {step.status === "completed" && "✓"}
                      {step.status === "rejected" && "✕"}
                    </div>

                    {/* Label */}
                    <div
                      className={`mt-2 text-center small fw-semibold ${step.status === "rejected" ? "text-danger" : ""
                        }`}
                    >
                      {step.label}
                    </div>

                    {/* Date */}
                    <div className="text-muted small text-center">
                      {step.date
                        ? new Date(step.date).toLocaleString()
                        : ""}
                    </div>

                  </div>
                ))}

              </div>
            </div>

          </ModalWrapper>
        );

      })()}{/* TRACK FLOW MODAL */}
      {modalType === "track" && (() => {

        const formatFlow = (data) => {
          return data.map((item, index) => {
            let label = "";

            if (index === 0) {
              label = "Requested by Employee";
            } else {
              const actionText =
                item.action === "APPROVE"
                  ? "Approved"
                  : item.action === "REJECT"
                    ? "Rejected"
                    : "";

              label = `${actionText} by ${item?.role?.role_title || ""}`;
            }

            return {
              label,
              date: item.created_at,
              status:
                item.action === "REJECT"
                  ? "rejected"
                  : "completed",
            };
          });
        };

        const steps = formatFlow(requestFlow || []);

        const rejectedIndex = steps.findIndex(
          (s) => s.status === "rejected"
        );

        const totalSteps = steps.length;

        const progressWidth =
          rejectedIndex !== -1
            ? ((rejectedIndex + 1) / totalSteps) * 100
            : (totalSteps / totalSteps) * 100;

        const progressColor =
          rejectedIndex !== -1 ? "#dc3545" : "#198754";

        return (
          <ModalWrapper onClose={closeModal}>

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-4">

              <h5 className="fw-bold mb-0">Approval Flow</h5>

              <button
                onClick={closeModal}
                style={{
                  border: "none",
                  background: "transparent",
                  fontSize: "20px",
                  cursor: "pointer",
                  lineHeight: "1"
                }}
              >
                ✕
              </button>

            </div>

            {/* FLOW */}
            <div>

              <div className="d-flex justify-content-between align-items-center position-relative">

                {/* Base Line */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "0",
                    right: "0",
                    height: "2px",
                    background: "#dee2e6",
                    zIndex: 0,
                  }}
                />

                {/* Progress Line */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "0",
                    width: `${progressWidth}%`,
                    height: "2px",
                    background: progressColor,
                    zIndex: 1,
                  }}
                />

                {/* Steps */}
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className="d-flex flex-column align-items-center flex-fill position-relative"
                  >

                    {/* Circle */}
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        backgroundColor:
                          step.status === "completed"
                            ? "#198754"
                            : step.status === "rejected"
                              ? "#dc3545"
                              : "#adb5bd",
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {step.status === "completed" && "✓"}
                      {step.status === "rejected" && "✕"}
                    </div>

                    {/* Label */}
                    <div
                      className={`mt-2 text-center small fw-semibold ${step.status === "rejected" ? "text-danger" : ""
                        }`}
                    >
                      {step.label}
                    </div>

                    {/* Date */}
                    <div className="text-muted small text-center">
                      {step.date
                        ? new Date(step.date).toLocaleString()
                        : ""}
                    </div>

                  </div>
                ))}

              </div>
            </div>

          </ModalWrapper>
        );

      })()}

      {/* DELETE MODAL */}
      {modalType === "delete" && (
        <ModalWrapper onClose={closeModal}>
          <h5 className="fw-bold mb-3">Delete Request</h5>
          <p>
            Are you sure you want to delete{" "}
            <strong>{selectedRequest?.learning_title}</strong>?
          </p>

          <div className="d-flex gap-2">
            <button
              className="btn btn-danger flex-fill"
              onClick={handleDelete}
            >
              Delete
            </button>

            <button
              className="btn btn-outline-secondary flex-fill"
              onClick={closeModal}
            >
              Cancel
            </button>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
};

export default ExternalLearningRequestList;